/**
 * InsightsPage — rendered at /app/project/:projectId/insights (nested under
 * ProjectDataLoader, which fetches and hydrates the project before this
 * component ever mounts — see src/hooks/useProjectData.ts).
 *
 * Dedicated page for managing project insights. Built on the shared AppPage
 * layout (see ./AppPage) so it inherits the same app-level font, colour
 * tokens, and header/body structure as every other non-canvas page.
 */
import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useOutletContext } from 'react-router-dom';
import { AppShellContext } from './AppShell';
import AppPage from './AppPage';
import AppPagePanel from 'components/ui/AppPagePanel';
import { useStore } from 'store/useStore';
import { saveProjectOst, saveOst, saveCustomAiInstructions } from 'api/projects';
import { isErrorResponse } from 'utils/apiUtils';
import { ProjectModel, NewInsightPayload, InsightNode, InsightsSummary, SuggestedOpportunity } from 'types';
import { InsightFormState } from 'components/ui/InsightForm';
import { getUniqueInsightUserNames } from 'components/panels/InsightsPanel';
import useDragSortList from 'hooks/useDragSortList';
import { DRAG_SORT_INDEX_ATTR, dragSortItemClassName, dragSortTranslateStyle } from 'utils/dragSortList';
import InsightPageCard from './InsightPageCard';
import InsightsSummaryPanel from './InsightsSummaryPanel';
import CustomInstructionsPanel from './CustomInstructionsPanel';
import OpportunitySuggestionsPanel from './OpportunitySuggestionsPanel';
import AddInsightModal from 'components/modals/AddInsightModal';
import { usePricingStore, UpgradeMessageKey } from '../../pricing';
import { canAddInsight } from '../../pricing/limits';

/** Normalize a project's persisted insightsSummary (legacy null / single-object / array) to an array. */
function normalizeSummaries(raw: unknown): InsightsSummary[] {
    if (Array.isArray(raw)) return raw as InsightsSummary[];
    if (raw && typeof raw === 'object') return [raw as InsightsSummary];
    return [];
}

/** Normalize a project's persisted suggestedOpportunities to an array. */
function normalizeSuggestedOpportunities(raw: unknown): SuggestedOpportunity[] {
    return Array.isArray(raw) ? (raw as SuggestedOpportunity[]) : [];
}

function insightToFormState(insight: InsightNode): InsightFormState {
    return {
        title: insight.title,
        description: insight.description ?? '',
        sourceUserName: insight.sourceUser.name,
        sourceUserIdentifier: insight.sourceUser.identifier ?? '',
        sourceUserIdentifierType: insight.sourceUser.identifierType ?? 'email',
        sourceUrl: insight.sourceUrl ?? '',
        cornerColor: insight.cornerColor ?? '',
    };
}

/** Matches `.insights-list-panel__list`'s own CSS `gap` (app.css) — see
 * getDragSortTranslateY's doc comment. */
const INSIGHTS_PAGE_LIST_GAP_PX = 8;

export default function InsightsPage() {
    const { projectId } = useParams<{ projectId: string }>();
    const { showToast } = useOutletContext<AppShellContext>();

    const {
        insights: rawInsights,
        insightLinks: rawInsightLinks,
        reorderInsights,
        addInsight,
        updateInsight,
        deleteInsight,
        setActiveProject,
        activeProject,
        user,
    } = useStore();

    const [filterUser, setFilterUser] = useState<string | null>(null);
    const [summaries, setSummaries] = useState<InsightsSummary[]>([]);
    const [suggestedOpportunities, setSuggestedOpportunities] = useState<SuggestedOpportunity[]>([]);
    const [isGeneratingOpps, setIsGeneratingOpps] = useState(false);
    // `customAiInstructions` is the value persisted on the server; `draftInstructions` is the
    // live, possibly-unsaved text in the textarea. Generation reads the DRAFT so instructions
    // typed just before clicking Update Summary / Regenerate All are applied immediately —
    // without waiting for the on-blur save round-trip to land.
    const [customAiInstructions, setCustomAiInstructions] = useState('');
    const [draftInstructions, setDraftInstructions] = useState('');

    type ModalState = { mode: 'add' } | { mode: 'edit'; insight: InsightNode };
    const [modalState, setModalState] = useState<ModalState | null>(null);

    // Project data (including insightsSummary / suggestedOpportunities /
    // customAiInstructions) is already fetched and hydrated into activeProject
    // by ProjectDataLoader before this page mounts — see
    // src/hooks/useProjectData.ts. Seed the local panel state from it whenever
    // we arrive at a (new or cached) project; no fetch needed here.
    useEffect(() => {
        if (!activeProject || activeProject._id !== projectId) return;
        setSummaries(normalizeSummaries((activeProject as any).insightsSummary));
        setSuggestedOpportunities(normalizeSuggestedOpportunities((activeProject as any).suggestedOpportunities));
        const cachedInstructions = (activeProject as any).customAiInstructions || '';
        setCustomAiInstructions(cachedInstructions);
        setDraftInstructions(cachedInstructions);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeProject?._id]);
    const insights = useMemo(() => rawInsights ?? [], [rawInsights]);
    const insightLinks = useMemo(() => rawInsightLinks ?? [], [rawInsightLinks]);
    const existingUsers = useMemo(() => getUniqueInsightUserNames(insights), [insights]);

    const trees = useMemo(() => {
        if (!activeProject) return [];
        const osts = (activeProject as any).osts;
        if (Array.isArray(osts) && osts.length > 0) {
            return osts.map((o: any) => ({ id: String(o._id), name: o.name || 'Unnamed Tree' }));
        }
        return [{ id: null as string | null, name: activeProject.name || 'Main Tree' }];
    }, [activeProject]);

    /**
     * Updates both the local panel state and the cached activeProject so that the
     * suggestions survive navigation (loaded from activeProject on re-entry) and a
     * cleared list is not resurrected by the store's fast path.
     */
    const syncSuggestedOpportunities = useCallback((opps: SuggestedOpportunity[]) => {
        setSuggestedOpportunities(opps);
        const current = useStore.getState().activeProject;
        if (current && current._id === projectId) {
            setActiveProject({ ...current, suggestedOpportunities: opps } as ProjectModel);
        }
    }, [projectId, setActiveProject]);

    /**
     * Updates both the local summaries state and the cached activeProject so that a
     * generated or deleted summary is reflected immediately on re-entry (the page
     * seeds from activeProject) instead of showing the stale cached list.
     */
    const applySummaries = useCallback((next: InsightsSummary[]) => {
        setSummaries(next);
        const current = useStore.getState().activeProject;
        if (current && current._id === projectId) {
            setActiveProject({ ...current, insightsSummary: next } as ProjectModel);
        }
    }, [projectId, setActiveProject]);

    /**
     * Persists the custom AI instructions used to tailor summary/opportunity generation.
     * The server re-validates with the same guardrail applied at generation time; on
     * rejection this resolves with the error message instead of updating any state, so
     * the panel can surface it without silently discarding the user's typed text.
     */
    const persistCustomAiInstructions = useCallback(async (text: string): Promise<string | null> => {
        if (!projectId) return null;
        const result = await saveCustomAiInstructions(projectId, text);
        if (result.error) return result.error;

        const saved = result.customAiInstructions ?? text;
        setCustomAiInstructions(saved);
        // Re-sync the draft to the server-normalized value (e.g. trimmed) so the two do not drift.
        setDraftInstructions(saved);
        const current = useStore.getState().activeProject;
        if (current && current._id === projectId) {
            setActiveProject({ ...current, customAiInstructions: saved } as ProjectModel);
        }
        return null;
    }, [projectId, setActiveProject]);

    const displayedInsights = useMemo(() =>
        insights
            .map((insight, index) => ({ insight, index }))
            .filter(({ insight }) => !filterUser || insight.sourceUser?.name?.trim() === filterUser),
        [insights, filterUser],
    );

    const [listAtTop, setListAtTop] = useState(true);
    const listRef = useRef<HTMLDivElement>(null);
    const listScrollRef = useRef<HTMLDivElement>(null);

    const handleListScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        setListAtTop(e.currentTarget.scrollTop === 0);
    }, []);

    const persistInsights = useCallback(async () => {
        const state = useStore.getState();
        const project = state.activeProject;
        if (!state.user || !project) return;

        const ost = {
            outcome: state.outcome,
            opportunities: state.opportunities,
            solutions: state.solutions,
            userStories: state.userStories,
            positions: state.positions,
            nodePanelPosition: state.nodePanelPosition,
            solutionChildType: state.solutionChildType,
            connections: state.connections,
            insights: state.insights,
            insightLinks: state.insightLinks,
            triageItems: state.triageItems,
        };

        const result = state.activeOstId
            ? await saveOst((project as any)._id, state.activeOstId, ost, state.insights)
            : await saveProjectOst((project as any)._id, ost, state.insights);

        if (isErrorResponse(result)) {
            showToast(`Save failed: ${result.error}`);
        }
    }, [showToast]);

    const handleReorder = useCallback(
        (fromIndex: number, toIndex: number) => {
            reorderInsights(fromIndex, toIndex);
            persistInsights();
        },
        [reorderInsights, persistInsights],
    );
    const { dragState, beginDrag, getTranslateY } = useDragSortList({
        itemCount: insights.length,
        containerRef: listRef,
        onReorder: handleReorder,
        gap: INSIGHTS_PAGE_LIST_GAP_PX,
    });

    const handleDragHandleMouseDown = useCallback(
        (e: React.MouseEvent, insightId: string) => {
            const index = insights.findIndex((n) => n.id === insightId);
            beginDrag(e, index);
        },
        [insights, beginDrag],
    );

    const handleAddNewInsight = () => {
        if (!canAddInsight(user, insights.length)) {
            usePricingStore.getState().showUpgradePrompt(UpgradeMessageKey.InsightLimit);
            return;
        }
        setModalState({ mode: 'add' });
    };

    const handleEditInsight = (insight: InsightNode) => {
        setModalState({ mode: 'edit', insight });
    };

    const handleModalConfirm = (payload: NewInsightPayload) => {
        if (modalState?.mode === 'edit') {
            updateInsight(modalState.insight.id, {
                title: payload.title,
                description: payload.description,
                sourceUser: payload.sourceUser,
                sourceUrl: payload.sourceUrl,
                cornerColor: payload.cornerColor,
            });
        } else {
            addInsight(payload);
        }
        setModalState(null);
        persistInsights();
    };

    const handleDeleteInsight = async () => {
        if (modalState?.mode !== 'edit') return;
        const id = modalState.insight.id;
        deleteInsight(id);
        setModalState(null);
        await persistInsights();
    };

    const ghostInsight = dragState ? insights[dragState.fromIndex] : null;

    return (
        <>
            <AppPage>
                <AppPagePanel
                    title={`All Insights${insights.length > 0 ? ` (${insights.length})` : ''}`}
                    style={{ flex: '0 0 30%', width: '30%', maxWidth: 460, minWidth: 0 }}
                    className='insights-list-panel'
                >
                    <button className='insights-list-panel__add-btn' onClick={handleAddNewInsight}>
                        + Add New Insight
                    </button>
                    {filterUser && (
                        <div className='insights-list-panel__filter-chip'>
                            <span className='insights-list-panel__filter-label'>
                                <span style={{ fontWeight: 400 }}>Filtered by User: </span>{filterUser}
                            </span>
                            <button
                                className='insights-list-panel__filter-clear'
                                onClick={() => setFilterUser(null)}
                                title='Clear filter'
                            >
                                ×
                            </button>
                        </div>
                    )}
                    {insights.length === 0 ? (
                        <p className='insights-list-panel__empty'>No insights yet.</p>
                    ) : displayedInsights.length === 0 ? (
                        <p className='insights-list-panel__empty'>No insights from {filterUser}.</p>
                    ) : (
                        <div
                            ref={listScrollRef}
                            className={`insights-list-panel__list-scroll${listAtTop ? ' insights-list-panel__list-scroll--at-top' : ''}`}
                            onScroll={handleListScroll}
                        >
                        <div ref={listRef} className='insights-list-panel__list'>
                            {displayedInsights.map(({ insight, index }) => {
                                const translateY = getTranslateY(index);
                                const isBeingDragged = dragState?.fromIndex === index;
                                return (
                                    <div
                                        key={insight.id}
                                        {...{ [DRAG_SORT_INDEX_ATTR]: index }}
                                        className={dragSortItemClassName('insights-page-list-item', !!dragState, isBeingDragged)}
                                        style={dragSortTranslateStyle(translateY)}
                                    >
                                        <InsightPageCard
                                            insight={insight}
                                            insightLinks={insightLinks.filter((l) => l.insightId === insight.id)}
                                            onDragHandleMouseDown={handleDragHandleMouseDown}
                                            onUserTagClick={setFilterUser}
                                            onEditClick={handleEditInsight}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                        </div>
                    )}
                </AppPagePanel>

                <div style={{ flex: '0 0 30%', width: '30%', maxWidth: 460, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '20px', minHeight: 0, overflow: 'hidden' }}>
                    <InsightsSummaryPanel
                        projectId={projectId!}
                        insights={insights}
                        summaries={summaries}
                        onSummaryGenerated={(s) => applySummaries([s, ...summaries].slice(0, 10))}
                        onSummaryDeleted={(generatedAt) => applySummaries(summaries.filter((s) => s.generatedAt !== generatedAt))}
                        onOpportunitiesSuggested={(opps) => syncSuggestedOpportunities(opps)}
                        onOpportunityGeneratingChange={setIsGeneratingOpps}
                        existingUsers={existingUsers}
                        onUserClick={setFilterUser}
                        customInstructions={draftInstructions}
                    />

                    <CustomInstructionsPanel
                        value={draftInstructions}
                        persisted={customAiInstructions}
                        onChange={setDraftInstructions}
                        onCommit={persistCustomAiInstructions}
                    />
                </div>

                <div style={{ flex: '0 0 30%', width: '30%', maxWidth: 460, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                    <OpportunitySuggestionsPanel
                        projectId={projectId!}
                        opportunities={suggestedOpportunities}
                        trees={trees}
                        isGenerating={isGeneratingOpps}
                        onOpportunitiesAdded={() => {
                            syncSuggestedOpportunities([]);
                            showToast('Opportunities added to tree successfully.');
                        }}
                        onError={(msg) => showToast(msg)}
                    />
                </div>
            </AppPage>

            {modalState && (
                <AddInsightModal
                    onConfirm={handleModalConfirm}
                    onClose={() => setModalState(null)}
                    onDelete={modalState.mode === 'edit' ? handleDeleteInsight : undefined}
                    initialValues={modalState.mode === 'edit' ? insightToFormState(modalState.insight) : undefined}
                    existingUsers={existingUsers}
                />
            )}

            {ghostInsight &&
                dragState &&
                createPortal(
                    <div
                        className='insight-page-card-ghost drag-ghost'
                        style={{
                            '--ghost-top': `${dragState.mouseY - dragState.offsetY}px`,
                            '--ghost-left': `${dragState.ghostX}px`,
                            '--ghost-width': `${dragState.ghostWidth}px`,
                        } as React.CSSProperties}
                    >
                        <InsightPageCard
                            insight={ghostInsight}
                            insightLinks={insightLinks.filter((l) => l.insightId === ghostInsight.id)}
                            onDragHandleMouseDown={() => { }}
                            onUserTagClick={() => { }}
                        />
                    </div>,
                    document.body,
                )}
        </>
    );
}
